import { useTranslation } from '../../../application/hooks/useTranslation';
import type { CommissionLedgerEntry } from '../../../domain/entities/CommissionLedgerEntry';
import { TIER_DESIGNATION_MAP } from '../../../domain/enums/TierLevel';
import { formatDhakaDate } from '../../../domain/formatters/date';
import { AmountDisplay } from '../ui/AmountDisplay';
import { Badge } from '../ui/Badge';
import { BottomSheet } from '../ui/BottomSheet';
import { typeLabel } from './ledgerLabels';
import './commissions.css';

export interface LedgerEntryDetailProps {
  entry: CommissionLedgerEntry | null;
  onClose: () => void;
}

/**
 * Amount-first receipt in the shared BottomSheet: credited amount, Dhaka date and type chip on
 * top, then who it was earned from and in which designation. No rates or explanatory notes.
 */
export function LedgerEntryDetail({ entry, onClose }: LedgerEntryDetailProps) {
  const { t, locale } = useTranslation();
  const label = entry ? typeLabel(entry, t, locale) : '';
  const designation = entry ? TIER_DESIGNATION_MAP[entry.earnerTier] : undefined;

  return (
    <BottomSheet open={entry !== null} title={t('commissions.detailTitle')} onClose={onClose} testId="ledger-detail">
      {entry && (
        <div className="ledger-receipt" data-testid="ledger-detail-body">
          <header className="ledger-receipt-head">
            <p className="ledger-receipt-amount">
              <span className="sr-only">{t('commissions.detailAmountLabel')}: </span>
              <AmountDisplay amount={entry.amount} bold />
            </p>
            <p className="ledger-receipt-date">{formatDhakaDate(entry.createdAt, locale)}</p>
            <Badge
              tone={entry.poolType === 'BOOKING_POOL' ? 'info' : 'accent'}
              role="img"
              aria-label={t('commissions.typeAria', { type: label })}
              data-testid="ledger-detail-chip"
            >
              {label}
            </Badge>
          </header>
          <dl className="ledger-receipt-list">
            <div className="ledger-receipt-row">
              <dt>{t('commissions.detailEarnedFrom')}</dt>
              <dd>{entry.sourceUserName}</dd>
            </div>
            {designation && (
              <div className="ledger-receipt-row">
                <dt>{t('commissions.detailDesignation')}</dt>
                <dd>{designation}</dd>
              </div>
            )}
          </dl>
        </div>
      )}
    </BottomSheet>
  );
}
